/* eslint-disable no-console */

// Edge runtime detection - completely safe for all environments
const isEdgeRuntime = () => {
  try {
    // Primary check: Next.js runtime environment variable
    if (typeof process !== "undefined" && process?.env) {
      const runtime = process.env.NEXT_RUNTIME;
      if (runtime === "edge" || runtime === "experimental-edge") {
        return true;
      }
    }

    // Secondary check: Edge Runtime global marker
    if (typeof globalThis !== "undefined" && globalThis.__nextjs_edge_runtime) {
      return true;
    }

    // Tertiary check: Look for Edge Runtime specific globals
    if (
      typeof EdgeRuntime !== "undefined" ||
      (typeof globalThis !== "undefined" && globalThis.EdgeRuntime)
    ) {
      return true;
    }

    // Fourth check: Check if we're in middleware context
    if (typeof process !== "undefined" && typeof process.env !== "undefined") {
      // Middleware detection through specific environment patterns
      const isMiddleware =
        process.env.NEXT_RUNTIME === "edge" ||
        process.env.EDGE_RUNTIME === "1" ||
        typeof require === "undefined";
      if (isMiddleware) {
        return true;
      }
    }

    // Final fallback: Check for absence of Node.js specific APIs
    // This is safer than checking process.versions.node directly
    if (
      typeof process !== "undefined" &&
      typeof require === "undefined" &&
      typeof __dirname === "undefined" &&
      typeof __filename === "undefined"
    ) {
      return true;
    }

    return false;
  } catch (error) {
    // If any check fails, assume Edge Runtime for safety
    return true;
  }
};

// Log level mapping
const levelMap: { [key: string]: number } = {
  silent: -Infinity,
  fatal: 0,
  error: 0,
  warn: 1,
  log: 2,
  info: 3,
  success: 3,
  debug: 4,
  trace: 5,
  verbose: Infinity,
};

// Get initial log level (safe for all environments)
const getInitialLogLevel = (): number => {
  if (typeof process === "undefined") return 3; // Browser default

  try {
    const envLogLevel = process.env.CONSOLA_LEVEL || process.env.APP_LOG_LEVEL;

    if (envLogLevel) {
      const levelStr = envLogLevel.toLowerCase();
      if (levelMap[levelStr] !== undefined) {
        return levelMap[levelStr];
      }
      const parsedLevel = parseInt(envLogLevel, 10);
      if (!isNaN(parsedLevel) && parsedLevel >= -1 && parsedLevel <= 5) {
        return parsedLevel;
      }
    }

    return process.env.NODE_ENV === "production" ? 1 : 3;
  } catch (error) {
    return 3; // Fallback if process access fails
  }
};

const initialLogLevel = getInitialLogLevel();

// Simple console-only logger for Edge Runtime and Browser
function createConsoleLogger(level = initialLogLevel) {
  const shouldLog = (methodLevel: number) => methodLevel <= level;
  const formatTag = (tag?: string) => (tag ? `[${tag}]` : "");

  return {
    level,
    fatal: (tag: string = "", ...args: any[]) =>
      shouldLog(levelMap.fatal) && console.error(formatTag(tag), ...args),
    error: (tag: string = "", ...args: any[]) =>
      shouldLog(levelMap.error) && console.error(formatTag(tag), ...args),
    warn: (tag: string = "", ...args: any[]) =>
      shouldLog(levelMap.warn) && console.warn(formatTag(tag), ...args),
    log: (tag: string = "", ...args: any[]) =>
      shouldLog(levelMap.log) && console.log(formatTag(tag), ...args),
    info: (tag: string = "", ...args: any[]) =>
      shouldLog(levelMap.info) && console.info(formatTag(tag), ...args),
    success: (tag: string = "", ...args: any[]) =>
      shouldLog(levelMap.success) && console.info(formatTag(tag), "✓", ...args),
    debug: (tag: string = "", ...args: any[]) =>
      shouldLog(levelMap.debug) && console.debug(formatTag(tag), ...args),
    trace: (tag: string = "", ...args: any[]) =>
      shouldLog(levelMap.trace) && console.trace(formatTag(tag), ...args),

    withTag: (tag: string) => {
      const logger = createConsoleLogger(level);
      const tagPrefix = formatTag(tag);
      return {
        level: logger.level,
        fatal: (...args: any[]) => logger.fatal(tagPrefix, ...args),
        error: (...args: any[]) => logger.error(tagPrefix, ...args),
        warn: (...args: any[]) => logger.warn(tagPrefix, ...args),
        log: (...args: any[]) => logger.log(tagPrefix, ...args),
        info: (...args: any[]) => logger.info(tagPrefix, ...args),
        success: (...args: any[]) => logger.success(tagPrefix, ...args),
        debug: (...args: any[]) => logger.debug(tagPrefix, ...args),
        trace: (...args: any[]) => logger.trace(tagPrefix, ...args),
        withTag: (newTag: string) => logger.withTag(`${tag}:${newTag}`),
        withDefaults: () => logger.withTag(tag),
      };
    },
    withDefaults: () => createConsoleLogger(level),
  };
}

// Node.js logger with file logging (only for Node.js runtime)
function createNodeLogger(level = initialLogLevel) {
  let writeToFile: ((message: string, isError?: boolean) => void) | null = null;

  // Only initialize file logging in Node.js runtime (never in Edge)
  if (!isEdgeRuntime() && typeof window === "undefined") {
    try {
      // Import Node.js modules only if available (no eval needed)
      let fs: any;
      let path: any;

      // Try to require modules with error handling
      try {
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        fs = require("fs");
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        path = require("path");

        // Use safe path resolution - Edge Runtime compatible
        let currentDir = "/tmp"; // Safe fallback for all environments

        // Use environment variable or fallback - avoid process.cwd completely
        // This eliminates Edge Runtime warnings while maintaining functionality
        if (
          !isEdgeRuntime() &&
          typeof process !== "undefined" &&
          typeof require !== "undefined" &&
          typeof __dirname !== "undefined"
        ) {
          try {
            // Additional safety check - ensure we're not in middleware/edge
            const runtime = process.env?.NEXT_RUNTIME;
            if (runtime !== "edge" && runtime !== "experimental-edge") {
              // Use PWD environment variable as safe alternative to process.cwd()
              currentDir = process.env.PWD || process.env.INIT_CWD || "/tmp";
            }
          } catch (error) {
            // Environment variable access failed, use safe fallback
            currentDir = "/tmp";
          }
        }

        const logsDir = path.join(currentDir, "logs");
        if (!fs.existsSync(logsDir)) {
          fs.mkdirSync(logsDir, { recursive: true });
        }

        const allLogPath = path.join(logsDir, "all.log");
        const errorLogPath = path.join(logsDir, "errors.log");

        writeToFile = (message: string, isError = false) => {
          const timestamp = new Date().toISOString();
          const logEntry = `${timestamp} ${message}\n`;

          // Write to all.log
          fs.appendFileSync(allLogPath, logEntry);

          // Write to errors.log if it's an error
          if (isError) {
            fs.appendFileSync(errorLogPath, logEntry);
          }
        };
      } catch (requireError) {
        // Node.js modules not available - skip file logging
        writeToFile = null;
      }
    } catch (error) {
      console.warn("[Logger] File logging disabled:", error.message);
    }
  }

  const shouldLog = (methodLevel: number) => methodLevel <= level;
  const formatTag = (tag?: string) => (tag ? `[${tag}]` : "");

  const logWithFile =
    (consoleMethod: any, methodLevel: number, isError = false) =>
    (tag: string = "", ...args: any[]) => {
      if (shouldLog(methodLevel)) {
        const formattedTag = formatTag(tag);
        consoleMethod(formattedTag, ...args);

        if (writeToFile) {
          const message = `${formattedTag} ${args
            .map((arg) =>
              typeof arg === "object" ? JSON.stringify(arg) : String(arg),
            )
            .join(" ")}`;
          writeToFile(message, isError);
        }
      }
    };

  return {
    level,
    fatal: logWithFile(console.error, levelMap.fatal, true),
    error: logWithFile(console.error, levelMap.error, true),
    warn: logWithFile(console.warn, levelMap.warn),
    log: logWithFile(console.log, levelMap.log),
    info: logWithFile(console.info, levelMap.info),
    success: (tag: string = "", ...args: any[]) => {
      if (shouldLog(levelMap.success)) {
        const formattedTag = formatTag(tag);
        console.info(formattedTag, "✓", ...args);

        if (writeToFile) {
          const message = `${formattedTag} ✓ ${args
            .map((arg) =>
              typeof arg === "object" ? JSON.stringify(arg) : String(arg),
            )
            .join(" ")}`;
          writeToFile(message);
        }
      }
    },
    debug: logWithFile(console.debug, levelMap.debug),
    trace: logWithFile(console.trace, levelMap.trace),

    withTag: (tag: string) => {
      const logger = createNodeLogger(level);
      const tagPrefix = formatTag(tag);
      return {
        level: logger.level,
        fatal: (...args: any[]) => logger.fatal(tagPrefix, ...args),
        error: (...args: any[]) => logger.error(tagPrefix, ...args),
        warn: (...args: any[]) => logger.warn(tagPrefix, ...args),
        log: (...args: any[]) => logger.log(tagPrefix, ...args),
        info: (...args: any[]) => logger.info(tagPrefix, ...args),
        success: (...args: any[]) => logger.success(tagPrefix, ...args),
        debug: (...args: any[]) => logger.debug(tagPrefix, ...args),
        trace: (...args: any[]) => logger.trace(tagPrefix, ...args),
        withTag: (newTag: string) => logger.withTag(`${tag}:${newTag}`),
        withDefaults: () => logger.withTag(tag),
      };
    },
    withDefaults: () => createNodeLogger(level),
  };
}

// Create logger instance based on environment
let mainLoggerInstance;

if (typeof window === "undefined") {
  // Server-side
  if (isEdgeRuntime()) {
    // Edge Runtime - console only
    mainLoggerInstance = createConsoleLogger(initialLogLevel);
  } else {
    // Node.js Runtime - use file logger
    mainLoggerInstance = createNodeLogger(initialLogLevel);
  }
} else {
  // Browser environment - console only
  mainLoggerInstance = createConsoleLogger(initialLogLevel);

  // Check for cookie override in browser
  try {
    const cookieValue = document.cookie
      .split("; ")
      .find((row) => row.startsWith("log_level_override="));

    if (cookieValue) {
      const levelStr = cookieValue.split("=")[1];
      const level = parseInt(levelStr, 10);
      if (!isNaN(level) && level >= -1 && level <= 5) {
        mainLoggerInstance.level = level;
      }
    }
  } catch (error) {
    // Ignore cookie errors
  }
}

export const logger = mainLoggerInstance;

export const createLogger = (tag: string) => {
  return mainLoggerInstance.withTag(tag);
};
